import { RegisterClass } from "core/Misc/typeStore";
import { Vector3 } from "core/Maths/math.vector";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

export enum RewardVectorConstantType {
    Up,
    Forward,
    Left,
    Gravity
}

function OperationString(operation : RewardVectorConstantType) : string {
    if( typeof operation === 'number' )
        return RewardVectorConstantType[operation];
    return operation as string;
}

function StringOperation(op : string) {
    return (RewardVectorConstantType as any)[op];
}

/**
 * Block used to apply math functions
 */
export class RewardVectorConstant extends RewardGraphNode {
    /**
     * Gets or sets the operation applied by the block
     */
    @editableInPropertyPage("Property", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "Up", value: RewardVectorConstantType.Up },
            { label: "Forward", value: RewardVectorConstantType.Forward },
            { label: "Left", value: RewardVectorConstantType.Left },
            { label: "Gravity", value: RewardVectorConstantType.Gravity },
        ],
    })
    public property = RewardVectorConstantType.Up;

    /**
     * Create a new MathBlock
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerOutput("output", RewardGraphConnectionPointTypes.Vector3);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardVectorConstant";
    }

    /**
     * Gets the reward output component
     */
    public get output(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock() {
        let func: (state: RewardGraphBuildState) => any;

        switch (this.property) {
            case RewardVectorConstantType.Up: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext.getGlobalVectorProperty('up');
                        return new Vector3(0, 1, 0);
                    };
                break;
            }
            case RewardVectorConstantType.Forward: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext.getGlobalVectorProperty('forward');
                        return new Vector3(1, 0, 0);
                    };
                break;
            }
            case RewardVectorConstantType.Left: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext.getGlobalVectorProperty('left');
                        return new Vector3(0, 0, 1);
                    };
                break;
            }
            case RewardVectorConstantType.Gravity: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext.getGlobalVectorProperty('gravity');
                        return new Vector3(0, -9.8, 0);
                    };
                break;
            }
        }

        this.output._storedFunction = (state) => {
            return func(state);
        };
    }

    protected override _dumpPropertiesCode() {
        const codeString = super._dumpPropertiesCode() + `${this._codeVariableName}.operation = MELS.RewardVectorConstant.${RewardVectorConstantType[this.property]};\n`;
        return codeString;
    }

    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.property = OperationString(this.property);

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.property = StringOperation(serializationObject.property);
    }
}

RegisterClass("MELS.RewardVectorConstant", RewardVectorConstant);
